import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatDate(date) {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  });
}

export function formatDateTime(date) {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  });
}

export function formatTime(date) {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  });
}

// "YYYY-MM-DDTHH:mm" in IST, for datetime-local inputs
export function toISTInput(date) {
  if (!date) return '';
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(date)).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

// datetime-local value (entered as IST) -> ISO string with +05:30 offset
export function istInputToISO(value) {
  return value ? `${value.slice(0, 16)}:00+05:30` : undefined;
}

export function todayIST() {
  return toISTInput(new Date()).slice(0, 10);
}

export function formatCurrency(amount) {
  if (amount === undefined || amount === null) return '₹0.00';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(amount);
}

export function getInitials(name) {
  if (!name) return 'U';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function truncate(str, length = 50) {
  if (!str) return '';
  return str.length > length ? `${str.slice(0, length)}...` : str;
}

export function getStatusColor(status) {
  const colors = {
    // Lead statuses
    new: 'bg-blue-100 text-blue-800',
    contacted: 'bg-purple-100 text-purple-800',
    proposal_sent: 'bg-amber-100 text-amber-800',
    negotiation: 'bg-orange-100 text-orange-800',
    won: 'bg-green-100 text-green-800',
    lost: 'bg-red-100 text-red-800',
    // Task statuses
    pending: 'bg-slate-100 text-slate-800',
    in_progress: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    blocked: 'bg-red-100 text-red-800',
    // Ticket statuses
    open: 'bg-red-100 text-red-800',
    resolved: 'bg-green-100 text-green-800',
    closed: 'bg-slate-100 text-slate-800',
    // Visit statuses
    scheduled: 'bg-blue-100 text-blue-800',
    // Invoice statuses
    paid: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    // Readiness
    green: 'bg-green-100 text-green-800',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-slate-100 text-slate-800';
}

export function getPriorityColor(priority) {
  const colors = {
    low: 'bg-slate-100 text-slate-800',
    medium: 'bg-amber-100 text-amber-800',
    high: 'bg-red-100 text-red-800',
  };
  return colors[priority] || 'bg-slate-100 text-slate-800';
}

export function getClientTypeLabel(type) {
  const labels = {
    hospital: 'Hospital',
    lab: 'Laboratory',
    diagnostic: 'Diagnostic Center',
  };
  return labels[type] || type;
}

export function getNABHTypeLabel(type) {
  const labels = {
    entry_level: 'Entry Level',
    full_accreditation: 'Full Accreditation',
    nabl: 'NABL',
  };
  return labels[type] || type;
}

export const ACCREDITATION_STANDARDS = [
  { value: 'nabh_6th_new', label: 'NABH 6th Edition - New', group: 'NABH 6th Edition' },
  { value: 'nabh_6th_surveillance', label: 'NABH 6th Edition - Surveillance', group: 'NABH 6th Edition' },
  { value: 'nabh_6th_renew', label: 'NABH 6th Edition - Renewal', group: 'NABH 6th Edition' },
  { value: 'nabh_3rd_new', label: 'NABH 3rd Edition - New', group: 'NABH 3rd Edition' },
  { value: 'nabh_3rd_surveillance', label: 'NABH 3rd Edition - Surveillance', group: 'NABH 3rd Edition' },
  { value: 'nabh_3rd_renew', label: 'NABH 3rd Edition - Renewal', group: 'NABH 3rd Edition' },
  { value: 'eye_care_new', label: 'Eye Care Standard - New', group: 'Eye Care' },
  { value: 'eye_care_surveillance', label: 'Eye Care Standard - Surveillance', group: 'Eye Care' },
  { value: 'eye_care_renew', label: 'Eye Care Standard - Renewal', group: 'Eye Care' },
  { value: 'entry_level_new', label: 'NABH Entry Level - New', group: 'Entry Level' },
  { value: 'entry_level_renew', label: 'NABH Entry Level - Renewal', group: 'Entry Level' },
  { value: 'dental_care', label: 'Dental Care', group: 'Other' },
  { value: 'diagnostic_centre', label: 'Diagnostic Centre', group: 'Other' },
];

export function getAccreditationLabel(value) {
  const std = ACCREDITATION_STANDARDS.find(s => s.value === value);
  if (std) return std.label;
  // For dynamic standards not in the hardcoded list, format the code nicely
  if (!value) return '-';
  return value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

export function getPhaseLabel(phase) {
  const labels = {
    gap_analysis: 'Phase 1: Gap Analysis & Planning',
    application_implementation: 'Phase 2: Documentation & NABH Application',
    desktop_nc_closure: 'Phase 3: Implementation, Monitoring & Desktop NC Closure',
    pre_assessment: 'Phase 4: On-Site Assessment Planning',
    final_nc_closure: 'Phase 5: Post-Assessment Support',
  };
  return labels[phase] || phase;
}

export function getPhaseShortLabel(phase) {
  const labels = {
    gap_analysis: 'Gap Analysis',
    application_implementation: 'Documentation',
    desktop_nc_closure: 'Implementation',
    pre_assessment: 'Assessment',
    final_nc_closure: 'Post-Assessment',
  };
  return labels[phase] || phase;
}

export function getRoleLabel(role) {
  const labels = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    staff: 'Staff',
    client: 'Client',
  };
  return labels[role] || role;
}


export function getTaskStatusLabel(status) {
  const labels = {
    open: 'Open',
    in_progress: 'In Progress',
    completed_by_assignee: 'Completed by Assignee',
    pending_verification: 'Pending Verification',
    verified: 'Verified / Closed',
    rejected: 'Rejected / Rework',
    blocked: 'Blocked',
    pending: 'Open',
    completed: 'Verified',
  };
  return labels[status] || status;
}

export function getTaskStatusColor(status) {
  const colors = {
    open: 'bg-slate-100 text-slate-700',
    in_progress: 'bg-blue-100 text-blue-700',
    completed_by_assignee: 'bg-amber-100 text-amber-700',
    pending_verification: 'bg-orange-100 text-orange-700',
    verified: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    blocked: 'bg-gray-100 text-gray-600',
    pending: 'bg-slate-100 text-slate-700',
    completed: 'bg-green-100 text-green-700',
  };
  return colors[status] || 'bg-gray-100 text-gray-600';
}
